import { NgClass } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DoCheck,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnDestroy,
  Optional,
  Output,
  Self,
  ViewChild,
  ViewEncapsulation,
  booleanAttribute,
  forwardRef,
} from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { get, values } from 'lodash-es';
import { Subject, debounceTime } from 'rxjs';

import {
  CanUpdateErrorState,
  FormFieldControl,
  mixinErrorState,
} from '@aw/shared/components/form-field';
import { RippleDirective } from '@aw/shared/directives';
import { IdPipe } from '@aw/shared/pipes';

import { FileUploadDropDirective } from './directives/file-upload-drop.directive';
import { AcceptedFileType } from './enums/enum';

type OnChangeFn = (value: Array<File>) => void;

const _FileUploadComponentBase = mixinErrorState(class {});

// TODO: Test with Cypress since this is impossible to test with jest

@UntilDestroy()
@Component({
  standalone: true,
  selector: 'aw-file-upload',
  templateUrl: 'file-upload.component.html',
  styleUrls: ['./file-upload.component.scss'],
  imports: [NgClass, RippleDirective, IdPipe, FileUploadDropDirective],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: FormFieldControl,
      useExisting: forwardRef(() => FileUploadComponent),
    },
  ],
})
export class FileUploadComponent
  extends _FileUploadComponentBase
  implements
    AfterViewInit,
    DoCheck,
    OnDestroy,
    FormFieldControl<Array<File>>,
    ControlValueAccessor,
    CanUpdateErrorState
{
  static nextId = 0;

  @HostBinding('attr.id')
  readonly id = `aw-file-upload-${FileUploadComponent.nextId++}`;

  @HostBinding('class.aw-file-upload')
  readonly identifierClass = true;

  @Input()
  set value(_value: Array<File> | null) {
    this._value = _value || [];
    this.stateChanges.next();
  }

  get value(): Array<File> {
    return this._value;
  }

  /**
   * Controls wether multiple files can be attached via native file browser or drag and dropped
   * If this is false and multiple files are dropped only the first one will be emitted
   */
  @Input({ transform: booleanAttribute }) multiple = false;

  @Input({ transform: booleanAttribute }) disabled = false;

  @Input() accept: Array<AcceptedFileType> = [AcceptedFileType.All];

  @Output() readonly valueChange = new EventEmitter<Array<File>>();

  get acceptAttr(): string {
    return this.accept.join(', ').trim();
  }

  constructor(
    @Optional() @Self() readonly ngControl: NgControl,
    private readonly changeDetector: ChangeDetectorRef,
  ) {
    super();

    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  @ViewChild('input', { static: true })
  private readonly input?: ElementRef<HTMLInputElement>;

  private _value: Array<File> = [];

  private _multiple = false;

  private onChange: OnChangeFn;

  private onTouched: () => void;

  private readonly openFileBrowser$ = new Subject<void>();

  ngDoCheck(): void {
    if (this.ngControl) {
      this.updateErrorState();
    }
  }

  ngAfterViewInit(): void {
    if (this.input) {
      this.initFileBrowserOpenHandler(this.input.nativeElement);
    }
  }

  onOpenFileBrowser(event: MouseEvent): void {
    this.openFileBrowser$.next();

    event.preventDefault();
    event.stopImmediatePropagation();
    event.stopPropagation();
  }

  onFilesDropped(fileList: FileList): void {
    this.emitValue(values(fileList));

    if (this.onTouched) {
      this.onTouched();
    }
  }

  onFilesAttached(event: Event): void {
    if (this.disabled) {
      this.clearInputValue();

      return;
    }

    const files = this.getFilesFromInputEvent(event);

    this.clearInputValue();

    if (this.onTouched) {
      this.onTouched();
    }

    this.emitValue(files);
  }

  writeValue(value: Array<File> | null): void {
    this.value = value;
    this.changeDetector.markForCheck();
  }

  registerOnChange(fn: OnChangeFn): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(disabled: boolean): void {
    this.disabled = disabled;
    this.changeDetector.markForCheck();
  }

  ngOnDestroy(): void {
    this.openFileBrowser$.complete();
  }

  private initFileBrowserOpenHandler(fileInput: HTMLInputElement): void {
    this.openFileBrowser$
      .pipe(debounceTime(200), untilDestroyed(this))
      .subscribe(() => fileInput.click());
  }

  private emitValue(files: Array<File>): void {
    let value: Array<File> = [];

    if (this.multiple) {
      value = files;
    } else {
      value = [files[0]];
    }

    if (this.onChange) {
      this.onChange(value);
    } else {
      this.valueChange.emit(value);
    }
  }

  private clearInputValue(): void {
    if (this.input) {
      /**
       *! This is critical in order to be able to attach the same file twice in a row.
       *! Needed in case the file is removed or some other interaction
       */
      this.input.nativeElement.value = '';
    }
  }

  private readonly getFilesFromInputEvent = (event: Event): Array<File> =>
    values(get(event, 'files', get(event, ['target', 'files'])));
}
